/**
 * The geometry contract for markup strokes (`KLQ-20260820-001` phase 3).
 *
 * `geometry_json` is `additionalProperties: true` on the wire — the server takes any object —
 * so this file is where its shape is written down. Every coordinate is normalised `0..1` in
 * the same frame as a pin's anchor, plus the page it is on. One discriminated union, five
 * members.
 *
 * **Defensive parsing.** A row whose `geometry_json` does not match its `kind` is skipped,
 * not thrown: it is somebody else's drawing and not this screen's crash.
 *
 * **The palette comes from `tokens.css` and is resolved at the moment of the write.**
 * `npm run guard` refuses a hex literal anywhere under `frontend/src`; the two meet in
 * `resolveColor`, which reads the computed value and normalises it to the wire pattern.
 */

import type { SheetPoint } from './overlay';

import type { Markup } from './api';

/** The five kinds the wire accepts. */
export type MarkupKind = 'draw' | 'line' | 'box' | 'text' | 'measure';

/**
 * What the rail can arm: a kind to draw, or the eraser. The eraser is not a `MarkupKind` — it
 * makes no row, it takes back rows this person drew in this session (`markupSession.ts`).
 */
export type MarkupTool = MarkupKind | 'erase';

const MARKUP_KINDS: readonly string[] = ['draw', 'line', 'box', 'text', 'measure'];

/** A row's `kind` column, which the response types as a plain string, read back as a kind. */
export function isMarkupKind(value: string): value is MarkupKind {
  return MARKUP_KINDS.includes(value);
}

export interface DrawGeometry {
  readonly kind: 'draw';
  readonly page: number;
  readonly points: readonly SheetPoint[];
}

export interface LineGeometry {
  readonly kind: 'line';
  readonly page: number;
  readonly from: SheetPoint;
  readonly to: SheetPoint;
}

export interface BoxGeometry {
  readonly kind: 'box';
  readonly page: number;
  readonly from: SheetPoint;
  readonly to: SheetPoint;
}

export interface TextGeometry {
  readonly kind: 'text';
  readonly page: number;
  readonly at: SheetPoint;
  readonly value: string;
}

export interface MeasureGeometry {
  readonly kind: 'measure';
  readonly page: number;
  readonly from: SheetPoint;
  readonly to: SheetPoint;
}

/** The five shapes a stroke can be, discriminated on `kind`. */
export type MarkupGeometry =
  | DrawGeometry
  | LineGeometry
  | BoxGeometry
  | TextGeometry
  | MeasureGeometry;

/** The five offered colours, as custom-property names. Never hex in source. */
export const MARKUP_PALETTE = [
  '--danger-500',
  '--accent-500',
  '--warning-500',
  '--success-500',
  '--neutral-900',
] as const;

/**
 * Resolve a custom-property name to the hex value the wire expects (`^#[0-9a-fA-F]{6}$`).
 *
 * The only place a hex value can appear at runtime: `getComputedStyle` reads what
 * `tokens.css` wrote, and this trims and upper-cases it. `npm run guard` refuses a hex
 * in source; this is where the two constraints meet.
 */
export function resolveColor(name: string): string {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name);
  return raw.trim().toUpperCase();
}

// --- Defensive parsing ---

function isSheetPoint(value: unknown): value is SheetPoint {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.x === 'number' && typeof v.y === 'number';
}

function isPage(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

/**
 * Parse a markup row's `geometry_json` into a typed `MarkupGeometry`.
 *
 * Returns `null` when the shape does not match the row's `kind` — the caller skips the row.
 * No `any`, no `as unknown as`: every field is checked with a real predicate.
 */
export function geometryOf(row: Markup): MarkupGeometry | null {
  const raw = row.geometry_json;
  const page = raw.page;
  if (!isPage(page)) return null;

  switch (row.kind) {
    case 'draw': {
      const points = raw.points;
      if (!Array.isArray(points) || points.length === 0) return null;
      if (!points.every(isSheetPoint)) return null;
      return { kind: 'draw', page, points };
    }
    case 'line': {
      const from = raw.from;
      const to = raw.to;
      if (!isSheetPoint(from) || !isSheetPoint(to)) return null;
      return { kind: 'line', page, from, to };
    }
    case 'box': {
      const from = raw.from;
      const to = raw.to;
      if (!isSheetPoint(from) || !isSheetPoint(to)) return null;
      return { kind: 'box', page, from, to };
    }
    case 'text': {
      const at = raw.at;
      const value = raw.value;
      if (!isSheetPoint(at) || typeof value !== 'string') return null;
      return { kind: 'text', page, at, value };
    }
    case 'measure': {
      const from = raw.from;
      const to = raw.to;
      if (!isSheetPoint(from) || !isSheetPoint(to)) return null;
      return { kind: 'measure', page, from, to };
    }
    default:
      return null;
  }
}
